import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { vaultWrite } from '../lib/vaultWrite';
import { ModalFrame } from '../ui/modal';
import { Avatar } from '../ui/avatar';
import { Toggle } from '../ui/toggle';
import { IconClose, IconUsers } from '../ui/icons';
import { groupByCategory } from './categories';

const LEVELS = [{ key: 'none', label: 'None' }, { key: 'viewer', label: 'Viewer' }, { key: 'revealer', label: 'Revealer' }];
const LEVEL_COLOR = { revealer: '#7c3aed', viewer: '#0F1419' };

// Stable hue per person so their avatar colour doesn't shuffle between
// renders — same trick as the nav's user hue, just derived from the code.
function hueFromCode(code) {
  let h = 0;
  for (let i = 0; i < (code || '').length; i++) h = (h * 31 + code.charCodeAt(i)) % 360;
  return h;
}

function LevelPicker({ value, onChange, disabled }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--border-default)', borderRadius: 999, padding: 2, gap: 2, background: 'var(--surface-elevated)' }}>
      {LEVELS.map(l => {
        const active = value === l.key;
        return (
          <button key={l.key} type="button" disabled={disabled} onClick={() => onChange(l.key)}
            style={{
              padding: '4px 11px', fontSize: 11, fontWeight: 600, borderRadius: 999, border: 'none',
              cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
              background: active ? (LEVEL_COLOR[l.key] || 'var(--surface-default-active)') : 'transparent',
              color: active ? '#fff' : 'var(--text-subdued)',
              opacity: disabled && !active ? 0.5 : 1,
              transition: 'background 120ms var(--ease-default), color 120ms var(--ease-default)',
            }}>{l.label}</button>
        );
      })}
    </div>
  );
}

// Access management, as its own modal (button-triggered from VaultPage)
// rather than a page tab — admin sets, per staff member, what they can
// access across every credential. User-centric per Arvee's ask.
export default function VaultAccessModal({ session, credentials, onClose }) {
  const [staff, setStaff] = useState([]);
  const [staffSearch, setStaffSearch] = useState('');
  const [selectedStaffCode, setSelectedStaffCode] = useState(null);
  const [staffGrants, setStaffGrants] = useState([]);
  const [updatingCredId, setUpdatingCredId] = useState(null);
  const [credSearch, setCredSearch] = useState('');
  const [togglingVault, setTogglingVault] = useState(false);
  const [hideAdmins, setHideAdmins] = useState(false);

  useEffect(() => {
    // Show every active staff member, not just people who already have the
    // `vault` page permission — Mo wants Manage Access to be the one place
    // to grant someone access, without a separate Admin Panel step first.
    aws.rpc('admin_staff_directory').then(({ data }) => setStaff((data || []).filter(s => s.active)));
  }, []);

  useEffect(() => {
    if (!selectedStaffCode) { setStaffGrants([]); return; }
    loadStaffGrants(selectedStaffCode);
  }, [selectedStaffCode]);

  async function loadStaffGrants(code) {
    const { data } = await aws.from('vault_credential_grants').select('*').eq('user_code', code).is('revoked_at', null);
    setStaffGrants(data || []);
  }

  // Sets one credential's access level for the selected staff member —
  // 'none' revokes an existing grant, a level on top of an existing grant
  // updates it, a level with no existing grant creates one.
  async function setAccessLevel(credentialId, newLevel) {
    setUpdatingCredId(credentialId);
    try {
      const existing = staffGrants.find(g => g.credential_id === credentialId);
      if (newLevel === 'none') {
        if (existing) await vaultWrite(session, 'vault_credential_grants', 'update', { revoked_at: new Date().toISOString() }, { column: 'id', value: existing.id });
      } else if (existing) {
        await vaultWrite(session, 'vault_credential_grants', 'update', { level: newLevel }, { column: 'id', value: existing.id });
      } else {
        await vaultWrite(session, 'vault_credential_grants', 'insert', {
          credential_id: credentialId, user_code: selectedStaffCode, level: newLevel, granted_by: session.code,
        });
      }
      await loadStaffGrants(selectedStaffCode);
    } catch (e) {
      alert('Could not update access: ' + (e.message || e));
    } finally {
      setUpdatingCredId(null);
    }
  }

  // Flips this person's `vault` page permission — the same setting Admin
  // Panel → Users → Page Access controls, exposed here too so granting
  // someone access doesn't need a separate trip to Admin Panel. Writes to
  // access_codes (not a vault_* table), so this goes through the normal
  // authWrite Lambda, not moreece-vault.
  async function toggleVaultPage(staffMember) {
    setTogglingVault(true);
    const nextValue = !(staffMember.permissions?.vault === true);
    try {
      await authWrite(session, 'access_codes', 'update',
        { permissions: { ...staffMember.permissions, vault: nextValue } },
        { column: 'code', value: staffMember.code });
      setStaff(prev => prev.map(s => s.code === staffMember.code ? { ...s, permissions: { ...s.permissions, vault: nextValue } } : s));
    } catch (e) {
      alert('Could not update page access: ' + (e.message || e));
    } finally {
      setTogglingVault(false);
    }
  }

  const selectedStaff = staff.find(s => s.code === selectedStaffCode);
  const filteredCredentials = useMemo(() => {
    const q = credSearch.trim().toLowerCase();
    if (!q) return credentials;
    return credentials.filter(c => (c.site_name || '').toLowerCase().includes(q) || (c.login_email || '').toLowerCase().includes(q) || (c.login_username || '').toLowerCase().includes(q));
  }, [credentials, credSearch]);
  const groups = useMemo(() => groupByCategory(filteredCredentials), [filteredCredentials]);

  return (
    <ModalFrame onClose={onClose}>
      <motion.div
        role="dialog"
        initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} transition={{ duration: 0.18 }}
        style={{
          width: 'min(960px, calc(100vw - 48px))',
          maxHeight: 'calc(100vh - 96px)',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(15,20,25,0.22), 0 2px 6px rgba(15,20,25,0.08)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subdued)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--surface-inverted)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconUsers size={14} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-emphasized)' }}>Manage Access</div>
              <div style={{ fontSize: 12, color: 'var(--text-subdued)', marginTop: 2 }}>Set what each staff member can see and reveal in the Vault</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subdued)', padding: 6, display: 'flex' }}>
            <IconClose size={16} />
          </button>
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <div style={{ width: 220, flexShrink: 0, borderRight: '1px solid var(--border-subdued)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 10, borderBottom: '1px solid var(--border-subdued)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input type="text" placeholder="Filter staff…" value={staffSearch} onChange={e => setStaffSearch(e.target.value)}
                className="form-input" style={{ width: '100%' }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-subdued)', cursor: 'pointer' }}>
                <input type="checkbox" checked={hideAdmins} onChange={e => setHideAdmins(e.target.checked)} />
                Hide admins
              </label>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {staff
                .filter(s => s.name?.toLowerCase().includes(staffSearch.trim().toLowerCase()))
                .filter(s => !hideAdmins || s.role !== 'admin')
                .map(s => {
                  const staffIsAdmin = s.role === 'admin';
                  const selected = selectedStaffCode === s.code;
                  return (
                    <div key={s.code} onClick={() => !staffIsAdmin && setSelectedStaffCode(s.code)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 12px', fontSize: 12, fontWeight: selected ? 600 : 400,
                        cursor: staffIsAdmin ? 'default' : 'pointer', opacity: staffIsAdmin ? 0.5 : 1,
                        background: selected ? 'var(--color-primary-soft)' : 'transparent',
                        borderLeft: selected ? '2px solid var(--color-primary)' : '2px solid transparent',
                        borderBottom: '1px solid var(--border-subdued)', color: 'var(--text-emphasized)',
                        transition: 'background 120ms var(--ease-default)',
                      }}>
                      <Avatar name={s.name} hue={hueFromCode(s.code)} size={22} />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                      {staffIsAdmin ? (
                        <span style={{ fontSize: 9, color: 'var(--text-disabled)', flexShrink: 0 }}>admin</span>
                      ) : (
                        <span title={s.permissions?.vault === true ? 'Vault page enabled' : 'Vault page not enabled'}
                          style={{ width: 6, height: 6, borderRadius: 999, flexShrink: 0, background: s.permissions?.vault === true ? '#16a34a' : 'var(--border-default)' }} />
                      )}
                    </div>
                  );
                })}
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {!selectedStaffCode ? (
              <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-subdued)', fontSize: 13 }}>Pick a staff member on the left to set what they can access.</div>
            ) : (<>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subdued)', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Avatar name={selectedStaff?.name} hue={hueFromCode(selectedStaffCode)} size={26} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-emphasized)', flex: 1 }}>{selectedStaff?.name}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-subdued)' }}>Vault access</span>
                  <Toggle checked={selectedStaff?.permissions?.vault === true} disabled={togglingVault} onChange={() => toggleVaultPage(selectedStaff)} />
                </div>
                {credentials.length > 0 && (
                  <input type="text" placeholder="Search accounts…" value={credSearch} onChange={e => setCredSearch(e.target.value)}
                    className="form-input" style={{ width: '100%' }} />
                )}
              </div>
              {credentials.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-subdued)', fontSize: 13 }}>No credentials exist yet — add one under Credentials first.</div>
              ) : groups.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-subdued)', fontSize: 12 }}>No accounts match "{credSearch}".</div>
              ) : groups.map(group => (
                <div key={group.key}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: 'var(--surface-elevated)' }}>
                    <span style={{ width: 7, height: 7, borderRadius: 999, background: group.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-emphasized)' }}>{group.label}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-subdued)' }}>{group.items.length}</span>
                  </div>
                  {group.items.map(c => {
                    const grant = staffGrants.find(g => g.credential_id === c.id);
                    const level = grant?.level || 'none';
                    return (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: '1px solid var(--border-subdued)' }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-emphasized)' }}>{c.site_name}</div>
                        <LevelPicker value={level} disabled={updatingCredId === c.id} onChange={v => setAccessLevel(c.id, v)} />
                      </div>
                    );
                  })}
                </div>
              ))}
            </>)}
          </div>
        </div>
      </motion.div>
    </ModalFrame>
  );
}
